/** @jsxImportSource @emotion/react */
import React from 'react'
import css from '@emotion/css/macro'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { isTimeToBeDefined } from '@sportninja/common/ts-utils/date-utils'
import CalendarIcon from 'src/components/icons/CalendarIcon'
import DirectionsIcon from 'src/components/icons/DirectionsIcon'

interface GameInfoBarProps {
  windowWidth: number
  startsAt?: string
  location?: {
    fullName: string
    addressString?: string
  }
  facility?: string
  fullGameType?: {
    name: string
  }
  isMobile?: boolean
  noBackground?: boolean
}

const GameInfoBar: React.FC<GameInfoBarProps> = ({
  windowWidth,
  startsAt,
  location,
  facility,
  fullGameType,
  isMobile = false,
  noBackground = false,
}) => {
  if (!startsAt && !location) {
    return null
  }

  const startTime = startsAt ? dayjs(startsAt) : null
  const isGameTimeToBeDefined = startsAt ? isTimeToBeDefined(startsAt) : false

  return (
    <div
      css={css`
        width: ${windowWidth}px;
        background-color: ${noBackground ? 'transparent' : colors.BLACK};
        display: flex;
        justify-content: center;
      `}
    >
      <div
        css={css`
          width: calc(100% - 36px);
          max-width: 1440px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: ${isMobile ? '8px 0' : '12px 12px 12px 0px'};
          gap: ${isMobile ? '8px' : '16px'};
          flex-direction: ${isMobile ? 'column' : 'row'};
          align-items: ${isMobile ? 'flex-start' : 'center'};
          box-sizing: border-box;
        `}
      >
        {/* Date, time and game type */}
        {startsAt && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 10px;
            `}
          >
            <CalendarIcon color={colors.PRIMARY} />
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 1px;
              `}
            >
              <span
                css={css`
                  color: ${colors.NEUTRAL_0};
                  font-size: ${isMobile ? '13px' : '14px'};
                  font-weight: 600;
                  line-height: 20px;
                `}
              >
                {isGameTimeToBeDefined
                  ? `${startTime!.format('MMM D, YYYY').toUpperCase()} - TBD`
                  : `${startTime!.format('h:mm A z')} - ${startTime!
                      .format('MMM D, YYYY')
                      .toUpperCase()}`}
              </span>
              {fullGameType?.name && (
                <span
                  css={css`
                    color: ${colors.NEUTRAL_50};
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 18px;
                  `}
                >
                  {fullGameType.name}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Venue and facility */}
        {location && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 10px;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: ${isMobile ? 'flex-start' : 'flex-end'};
                gap: 1px;
              `}
            >
              <span
                css={css`
                  color: ${colors.NEUTRAL_0};
                  font-size: ${isMobile ? '13px' : '14px'};
                  font-weight: 600;
                  line-height: 20px;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  text-align: ${isMobile ? 'left' : 'right'};
                `}
              >
                {location.fullName}
                {facility && ` - ${facility}`}
              </span>
              {!isMobile && location.addressString && (
                <span
                  css={css`
                    color: ${colors.NEUTRAL_50};
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 18px;
                    text-align: right;
                  `}
                >
                  {location.addressString}
                </span>
              )}
            </div>
            <DirectionsIcon color={colors.PRIMARY} />
          </div>
        )}
      </div>
    </div>
  )
}

export default GameInfoBar
